import React, { useRef, useState } from "react";
import { parseCOA } from "../api/client";

// A lab's collection date ("04/01/26 10:17", "4/1/2026", "2026-04-01T…") as the
// YYYY-MM-DD a date field takes, or "" when it can't be read.
export function coaDate(raw: string | null | undefined): string {
  const v = (raw ?? "").trim();
  let m = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  m = v.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})\b/);
  if (!m) return "";
  const year = m[3].length === 2 ? 2000 + parseInt(m[3]) : parseInt(m[3]);
  const month = parseInt(m[1]), day = parseInt(m[2]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return "";
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

interface Props {
  companyId: number;
  companyName: string;
  parameters?: any[];       // no longer used: hand-matching offers the permit's own limits
  onConfirm: (payload: any) => void;
  onCancel: () => void;
}

export default function COAImport({ companyId, companyName, onConfirm, onCancel }: Props) {
  const fileRef  = useRef<HTMLInputElement>(null);
  const [parsing,  setParsing]  = useState(false);
  const [file,     setFile]     = useState<File | null>(null);   // kept with the sample on submit
  const [preview,  setPreview]  = useState<any | null>(null);
  const [error,    setError]    = useState("");
  // Per-row overrides: rowKey → permit_limit_id chosen by the user (null = skip)
  const [overrides, setOverrides] = useState<Record<string, number | null>>({});

  const handleFile = async (file: File) => {
    setParsing(true);
    setError("");
    setPreview(null);
    setFile(file);
    try {
      const res = await parseCOA(companyId, file);
      setPreview(res.data);
    } catch (e: any) {
      setError(e.response?.data?.error ?? "Failed to parse PDF");
    } finally {
      setParsing(false);
    }
  };

  const rowKey = (sIdx: number, rIdx: number) => `${sIdx}-${rIdx}`;

  // The results of one sample on the report, ready to submit.
  const buildResults = (sIdx: number) => {
    const results: any[] = [];
    preview.samples[sIdx].results.forEach((r: any, rIdx: number) => {
      const key   = rowKey(sIdx, rIdx);
      const limId = overrides[key] !== undefined ? overrides[key] : r.permit_limit_id;
      if (!limId) return;
      // Results are stored in mg/L: use the server's converted values (a µg/L lab
      // value is divided by 1000), falling back to the lab value as printed.
      const value = r.result_mg_l ?? r.result;
      const rl    = r.reporting_limit_mg_l ?? r.reporting_limit ?? null;
      if (r.non_detect) {
        // A non-detect carries no measured value: the "<" number (or the lab's
        // reporting-limit column for a bare "ND") is the reporting limit.
        results.push({
          permit_limit_id: limId, concentration: null, non_detect: true,
          reported_limit_mg_l: value ?? rl,
        });
      } else if (value !== null) {
        results.push({ permit_limit_id: limId, concentration: value });
      }
    });
    return results;
  };

  // The sample the import brings in: the report's first sample. A report can print
  // one sample's results across pages as separate groups with the same sample ID
  // and collection time — those groups are all that one sample.
  const importSample = () => {
    if (!preview?.samples?.length) return { sample_date: "", results: [] as any[] };
    const first = preview.samples[0];
    const results: any[] = [];
    preview.samples.forEach((smp: any, i: number) => {
      if (smp.client_sample_id !== first.client_sample_id || smp.date_collected !== first.date_collected) return;
      for (const r of buildResults(i))
        if (!results.some(x => x.permit_limit_id === r.permit_limit_id)) results.push(r);
    });
    return { sample_date: coaDate(first.date_collected), results };
  };

  const handleConfirm = () => {
    if (!preview) return;
    const sample = importSample();
    onConfirm({
      permit_id:      preview.permit_id,
      sample_date:    sample.sample_date,
      coa_job_id:     preview.job_id,
      results:        sample.results,
      _file:          file,      // the lab report PDF itself, saved with the sample on submit
      _preview:       preview,   // passed through so the form can pre-fill the rest
    });
  };

  // ── Styles ──────────────────────────────────────────────────────────────
  const s: Record<string, React.CSSProperties> = {
    wrap:     { fontFamily:"inherit" },
    dropzone: { border:"2px dashed #b794f4", borderRadius:8, padding:"32px 24px",
                textAlign:"center", cursor:"pointer", background:"#faf5ff",
                color:"#553c9a", marginBottom:16 },
    table:    { width:"100%", borderCollapse:"collapse", fontSize:13, marginTop:12 },
    th:       { padding:"6px 10px", background:"#f7fafc", borderBottom:"2px solid #e2e8f0",
                textAlign:"left" as const, fontWeight:700, color:"#1a202c", whiteSpace:"nowrap" as const },
    td:       { padding:"6px 10px", borderBottom:"1px solid #edf2f7", verticalAlign:"middle" as const },
    btn:      { padding:"9px 22px", background:"#553c9a", color:"#fff",
                border:"none", borderRadius:5, fontWeight:600, cursor:"pointer" },
    outBtn:   { padding:"9px 22px", background:"#fff", color:"#718096",
                border:"2px solid #cbd5e0", borderRadius:5, fontWeight:600, cursor:"pointer" },
    select:   { padding:"3px 6px", border:"1px solid #cbd5e0", borderRadius:4,
                fontSize:12, maxWidth:200 },
    summary:  { display:"flex", gap:16, marginBottom:12, flexWrap:"wrap" as const },
  };

  const badge = (color: string, bg: string): React.CSSProperties => ({
    display:"inline-block", padding:"2px 8px", borderRadius:10,
    fontSize:11, fontWeight:700, background:bg, color,
  });
  const chip = (color: string, bg: string): React.CSSProperties => ({
    padding:"4px 12px", borderRadius:12, fontSize:12,
    fontWeight:700, background:bg, color,
  });

  return (
    <div style={s.wrap}>
      {/* ── Step 1: file picker ── */}
      {!preview && (
        <>
          <div style={s.dropzone}
            onClick={() => fileRef.current?.click()}
            onDragOver={e => e.preventDefault()}
            onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) handleFile(f); }}>
            {parsing
              ? "Parsing PDF…"
              : <>
                  <div style={{fontSize:32, marginBottom:8}}>📄</div>
                  <strong>Click or drag a COA PDF here</strong>
                  <div style={{fontSize:12, marginTop:4, color:"#718096"}}>
                    Eurofins Columbia format · results will be matched to {companyName}'s active permit
                  </div>
                </>
            }
          </div>
          <input ref={fileRef} type="file" accept=".pdf" style={{display:"none"}}
            onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f); }} />
          {error && <p style={{color:"#c53030", fontSize:13}}>{error}</p>}
          <button style={s.outBtn} onClick={onCancel}>Cancel</button>
        </>
      )}

      {/* ── Step 2: review ── */}
      {preview && (
        <>
          <div style={{marginBottom:12}}>
            <strong>{preview.source_file}</strong>
            <span style={{color:"#718096", fontSize:12, marginLeft:12}}>
              Client: {preview.client} · Job: {preview.job_id} · Permit: {preview.permit_number}
            </span>
          </div>

          <div style={s.summary}>
            <span style={chip("#276749","#c6f6d5")}>{preview.in_permit} in permit</span>
            <span style={chip("#b7791f","#fefcbf")}>{preview.matched - preview.in_permit} matched, not in permit</span>
            <span style={chip("#c53030","#fed7d7")}>{preview.unmatched} unmatched</span>
          </div>

          {preview.samples.map((sample: any, sIdx: number) => (
            <div key={sIdx} style={{marginBottom:20}}>
              <div style={{fontSize:13, color:"#4a5568", marginBottom:6}}>
                Sample: <strong>{sample.client_sample_id}</strong>
                {sample.date_collected && <> · Collected: <strong>{sample.date_collected}</strong></>}
                {sample.matrix && <> · Matrix: {sample.matrix}</>}
              </div>
              <div style={{overflowX:"auto"}}>
                <table style={s.table}>
                  <thead>
                    <tr>
                      <th style={s.th}>COA Analyte</th>
                      <th style={s.th}>Result</th>
                      <th style={s.th}>Unit</th>
                      <th style={s.th}>Matched Parameter</th>
                      <th style={s.th}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sample.results.map((r: any, rIdx: number) => {
                      const key      = rowKey(sIdx, rIdx);
                      const override = overrides[key];
                      const limId    = override !== undefined ? override : r.permit_limit_id;
                      const inPermit = override !== undefined
                        ? override !== null
                        : r.in_permit;

                      return (
                        <tr key={rIdx} style={{background: r.non_detect ? "#f7fafc" : "white"}}>
                          <td style={s.td}>{r.analyte}</td>
                          <td style={{...s.td, textAlign:"right"}}>
                            {r.non_detect
                              ? <span style={{color:"#718096"}}>ND (&lt;{r.result})</span>
                              : <strong>{r.result}</strong>
                            }
                          </td>
                          <td style={s.td}>
                            {r.unit}
                            {r.unit_converted && (r.result_mg_l ?? r.reporting_limit_mg_l) != null && (
                              <div style={{fontSize:11, color:"#2b6cb0"}}>
                                imports as {r.non_detect ? "<" : ""}{r.result_mg_l ?? r.reporting_limit_mg_l} mg/L
                              </div>
                            )}
                          </td>
                          <td style={s.td}>
                            {r.matched && override === undefined
                              ? r.parameter_name
                              : (
                                <select style={s.select}
                                  value={limId ?? ""}
                                  onChange={e => {
                                    const val = e.target.value ? parseInt(e.target.value) : null;
                                    setOverrides(o => ({...o, [key]: val}));
                                  }}>
                                  <option value="">— skip —</option>
                                  {(preview.permit_limits ?? []).map((pl: any) => (
                                    <option key={pl.id} value={pl.id}>{pl.parameter_name}</option>
                                  ))}
                                </select>
                              )
                            }
                          </td>
                          <td style={s.td}>
                            {r.non_detect
                              ? <span style={badge("#718096","#edf2f7")}>Non-detect</span>
                              : inPermit
                                ? <span style={badge("#276749","#c6f6d5")}>In permit</span>
                                : r.matched
                                  ? <span style={badge("#b7791f","#fefcbf")}>Not in permit</span>
                                  : <span style={badge("#c53030","#fed7d7")}>Unmatched</span>
                            }
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))}

          <p style={{fontSize:12, color:"#718096", marginBottom:12}}>
            Only rows marked <strong>In permit</strong> with numeric results will be imported.
            Non-detects are imported as non-detect with the lab's reporting limit; unmatched rows
            are excluded. Results the lab reported in µg/L are converted to mg/L. You can still adjust meter readings
            and other fields on the next screen.
          </p>

          <div style={{display:"flex", gap:10}}>
            <button style={s.btn} onClick={handleConfirm}>
              Import {importSample().results.length} results →
            </button>
            <button style={s.outBtn} onClick={() => { setPreview(null); setOverrides({}); }}>
              ← Try another file
            </button>
            <button style={s.outBtn} onClick={onCancel}>Cancel</button>
          </div>
        </>
      )}
    </div>
  );
}
